import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/use-auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLocation } from 'wouter';

function friendlyAuthError(message?: string) {
  if (!message) return '';
  if (/failed to fetch|network error|load failed/i.test(message)) {
    return 'We could not reach the server. Start the API, then try signing in again.';
  }
  return message;
}

function IconBubble({
  className,
  label,
  children,
}: {
  className: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`absolute flex h-14 w-14 items-center justify-center rounded-full border border-white/50 bg-white/25 text-white shadow-lg backdrop-blur-md ${className}`}
      title={label}
      aria-hidden="true"
    >
      {children}
    </div>
  );
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export default function AuthPage() {
  const { user, loginMutation, registerMutation } = useAuth();
  const [, setLocation] = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
  });

  useEffect(() => {
    if (user) {
      setLocation('/');
    }
  }, [user, setLocation]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isLogin) {
      loginMutation.mutate({
        email: formData.email.trim(),
        password: formData.password,
      });
    } else {
      registerMutation.mutate({
        email: formData.email.trim(),
        password: formData.password,
        full_name: formData.full_name.trim() || undefined,
      });
    }
  };

  const pending = loginMutation.isPending || registerMutation.isPending;
  const errorMessage = friendlyAuthError(
    (isLogin ? loginMutation.error : registerMutation.error)?.message
  );

  if (user) return null;

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0c3b1e] text-white">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-[#d7f5a8]/30 blur-3xl" />
        <div className="absolute right-10 top-0 h-80 w-80 rounded-full bg-[#7dffa2]/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-64 w-96 rounded-full bg-[#145c32]/80 blur-2xl" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#e8ffc8]/25 via-transparent to-[#04150a]/80" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col items-center gap-6 px-4 py-8 md:flex-row md:items-center md:justify-between md:gap-10 md:px-8">
        <section className="w-full max-w-xl md:w-1/2">
          <p className="text-sm font-medium uppercase tracking-[0.22em] text-[#d7f5a8]">
            Eco Ledger
          </p>
          <h1 className="mt-3 text-3xl font-semibold leading-tight text-white sm:text-5xl">
            Track the warming impact of every activity.
          </h1>
          <p className="mt-4 max-w-md text-base text-green-50/90">
            One place for Scope 1, 2, and 3, company teams, and a greenhouse gas inventory you can stand behind.
          </p>

          <div className="relative mx-auto mt-6 h-[240px] w-full max-w-md sm:h-[300px]">
            <div className="absolute bottom-6 left-1/2 h-16 w-64 -translate-x-1/2 rounded-[100%] bg-[#06210f]/80 blur-sm" />
            <div className="absolute bottom-8 left-1/2 h-10 w-56 -translate-x-1/2 rounded-[100%] bg-[#1f6b38]" />
            <div className="absolute bottom-10 left-1/2 h-8 w-40 -translate-x-1/2 rounded-[100%] bg-[#8fbf6a]/70" />

            <div className="absolute left-1/2 top-6 h-44 w-44 -translate-x-1/2 overflow-hidden rounded-full bg-gradient-to-br from-white/80 via-[#b7e7ff]/50 to-[#1f8a4c]/80 shadow-[0_20px_60px_rgba(0,0,0,0.25)] sm:top-8 sm:h-52 sm:w-52">
              <div className="absolute inset-3 rounded-full border border-white/40" />
              <div className="absolute inset-0 rounded-full border-x border-white/30" />
              <div className="absolute left-1/2 top-2 h-40 w-20 -translate-x-1/2 rounded-[100%] border border-white/30 sm:h-48 sm:w-24" />
              <div className="absolute left-6 right-6 top-1/2 h-px bg-white/40" />
              <div className="absolute left-8 right-8 top-[32%] h-px bg-white/25" />
              <div className="absolute left-8 right-8 top-[68%] h-px bg-white/25" />
              <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.85),transparent_42%)]" />
            </div>

            <IconBubble className="left-6 top-4" label="Carbon dioxide">
              <svg width="26" height="26" viewBox="0 0 24 24" {...stroke}>
                <path d="M7 18h10a4 4 0 0 0 0-8 5 5 0 0 0-9.5-1.5A3.5 3.5 0 0 0 7 18z" />
                <path d="M9 15h2.2a1.6 1.6 0 1 0 0-3.2H9V15zM13.5 15v-3.2" />
              </svg>
            </IconBubble>
            <IconBubble className="right-4 top-10" label="Global reporting">
              <svg width="26" height="26" viewBox="0 0 24 24" {...stroke}>
                <circle cx="12" cy="12" r="8" />
                <path d="M4 12h16M12 4c2.2 2.4 3.3 5 3.3 8s-1.1 5.6-3.3 8c-2.2-2.4-3.3-5-3.3-8s1.1-5.6 3.3-8z" />
              </svg>
            </IconBubble>
            <IconBubble className="right-0 top-40" label="Reuse and recovery">
              <svg width="26" height="26" viewBox="0 0 24 24" {...stroke}>
                <path d="M8 8l2.2-3.2L14 8" />
                <path d="M10.2 4.8A6.5 6.5 0 0 1 18 12" />
                <path d="M16 16l-2.2 3.2L10 16" />
                <path d="M13.8 19.2A6.5 6.5 0 0 1 6 12" />
              </svg>
            </IconBubble>
            <IconBubble className="left-0 top-28 sm:top-32" label="Clean energy">
              <svg width="26" height="26" viewBox="0 0 24 24" {...stroke}>
                <path d="M12 11v9" />
                <path d="M12 11l-6 3.2M12 11l6 3.2M12 11V4" />
                <path d="M8 21h8" />
              </svg>
            </IconBubble>
            <IconBubble className="bottom-2 left-16" label="Nature">
              <svg width="26" height="26" viewBox="0 0 24 24" {...stroke}>
                <path d="M12 21V10" />
                <path d="M12 13c-3-1-5-3.2-5-6 3 .2 5 2 5 6zM12 11c3-.6 5.2-2.6 5.5-5.5-3 .2-5 2.2-5.5 5.5z" />
              </svg>
            </IconBubble>
            <IconBubble className="bottom-4 right-14" label="Organization">
              <svg width="26" height="26" viewBox="0 0 24 24" {...stroke}>
                <path d="M4 20h16M6 20V9l6-4 6 4v11" />
                <path d="M10 20v-4h4v4M9 12h.01M12 12h.01M15 12h.01" />
              </svg>
            </IconBubble>
          </div>
        </section>

        <section className="w-full max-w-md md:w-[420px]">
          <div className="rounded-3xl border border-white/40 bg-white/95 p-7 text-gray-900 shadow-2xl backdrop-blur">
            <h2 className="text-2xl font-semibold">
              {isLogin ? 'Sign in' : 'Create an account'}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {isLogin
                ? 'Enter the email and password for your organization.'
                : 'Use your work email to start tracking emissions.'}
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              {!isLogin && (
                <Input
                  label="Full name"
                  value={formData.full_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, full_name: e.target.value }))}
                  placeholder="Your name"
                  autoComplete="name"
                  className="h-11"
                />
              )}

              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="you@company.com"
                autoComplete="email"
                required
                className="h-11"
              />

              <div>
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter your password"
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  required
                  minLength={isLogin ? undefined : 8}
                  className="h-11"
                />
                <button
                  type="button"
                  className="mt-2 text-sm text-green-800 hover:underline"
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? 'Hide password' : 'Show password'}
                </button>
              </div>

              {errorMessage && (
                <div
                  role="alert"
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                >
                  {errorMessage}
                </div>
              )}

              <Button type="submit" className="h-11 w-full bg-[#0f6b32] hover:bg-[#0c5528]" disabled={pending}>
                {pending ? 'Please wait…' : isLogin ? 'Sign in' : 'Create account'}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-600">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
              <button
                type="button"
                className="font-medium text-green-800 hover:underline"
                onClick={() => {
                  setIsLogin((value) => !value);
                  loginMutation.reset();
                  registerMutation.reset();
                }}
              >
                {isLogin ? 'Create one' : 'Sign in'}
              </button>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
